import * as analytics from '@actions/analytics';
import { getMetaEventId } from '@actions/analytics/meta';
import {
  createSessionAndRetrieveTokensV2,
  getCookieValueFromHisName,
} from '@actions/cookie';
import { getCurrentOrigin } from '@actions/formManipulation';
import { findFormation, type Theme } from '@core/theme';

const readUtm = (): Record<string, string> => {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = [
    'utm_id',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
  ].reduce<Record<string, string>>((acc, key) => {
    const value = params.get(key);
    if (value) {
      acc[key] = value;
    }

    return acc;
  }, {});

  const raw = localStorage.getItem('b24_crm_guest_utm');
  const fromStorage: Record<string, string> = raw
    ? Object.fromEntries(
        Object.entries(JSON.parse(raw).list as Record<string, string>).filter(
          ([name, value]) => name && value,
        ),
      )
    : {};

  return { ...fromUrl, ...fromStorage };
};

// The Bitrix24 guest script stores visited pages as [url, timestamp, title] tuples,
// oldest first.
type GuestPage = [url: string, timestamp: number, title: string];

const readFbclidFromGuestPages = (): string | undefined => {
  try {
    const pages: GuestPage[] = JSON.parse(
      localStorage.getItem('b24_crm_guest_pages') ?? '[]',
    );

    return (
      pages
        .map(([url]) => new URL(url).searchParams.get('fbclid'))
        .findLast((fbclid) => fbclid !== null) ?? undefined
    );
  } catch {
    return undefined;
  }
};

// The Meta pixel writes `_fbc` as `fb.<subdomainIndex>.<creationTime>.<fbclid>`.
const readFbclidFromMetaPixel = (): string | undefined => {
  const fbclid = getCookieValueFromHisName('_fbc')
    ?.split('.')
    .slice(3)
    .join('.');

  return fbclid || undefined;
};

// FIXME: ga_cid+GA_CID / session_anonyme+SESSION_ANONYME en double casse — le back
// (leadpubsub) lit les MAJUSCULES, le front historiquement les minuscules. Et u_IP,
// attendu par le back, n'a jamais de source côté front. À aligner (cf. point 8).
export const readTracking = (): Record<string, string> => {
  const {
    session_anonyme,
    ga_cid,
    fb_fbp,
    fbclid: fbclidCookie,
  } = createSessionAndRetrieveTokensV2();
  const utm = readUtm();
  const gclid = getCookieValueFromHisName('gclid') || utm['gclid'];
  const fbclid =
    fbclidCookie || readFbclidFromMetaPixel() || readFbclidFromGuestPages();
  const metaEventId = getMetaEventId();

  return {
    ...utm,
    origin: getCurrentOrigin() + window.location.pathname,
    ...(ga_cid ? { ga_cid, GA_CID: ga_cid } : {}),
    ...(session_anonyme
      ? { session_anonyme, SESSION_ANONYME: session_anonyme }
      : {}),
    ...(fb_fbp ? { fb_fbp } : {}),
    ...(fbclid ? { fbclid } : {}),
    ...(gclid ? { gclid } : {}),
    ...(metaEventId ? { metaEventId } : {}),
  };
};

export const emitBootstrapTracking = ({
  tracking,
  themes,
  idFormationBitrix,
}: {
  tracking: Record<string, string>;
  themes: Theme[];
  idFormationBitrix?: number;
}): void => {
  analytics.sendData({ event: 'setOrigin', origin: tracking.origin });

  Object.entries(tracking)
    .filter(([name]) => name.startsWith('utm_'))
    .forEach(([name, value]) =>
      analytics.sendData(
        analytics.formatTrackingEvent({ formData: { name, value } }),
      ),
    );

  if (idFormationBitrix === undefined) {
    return;
  }
  const formation = findFormation({ themes, id: idFormationBitrix });
  if (formation) {
    analytics.sendData({
      event: 'setThematique',
      idFormationBitrix: formation.id_formation_bitrix,
      formationId: formation.id,
      formation: formation.name,
    });
  }
};
